import { isArray, isObject } from "lodash-es";
import type React from "react";
import type { CSSProperties } from "react";
import DataTable, {
  type ConditionalStyles,
  type TableColumn,
} from "react-data-table-component";
import { Heading } from "../../../../components/Theme/heading";
import { ErrorRowInfoTooltipIcon } from "./ErrorRowInfoTooltipIcon";
import type { UploadDefinition } from "./GenericUploadPage";

interface UploadPreviewTableProps<T> {
  uploadedEntityName: string;
  definitions: UploadDefinition<T>[];
  requiredHeaders: string[];
  optionalHeaders?: string[];
  // Opt in to red / amber / green row backgrounds (mirroring the bulk
  // update preview): red = blocking errors, amber = warnings only,
  // green = clean. Default off preserves the original single-tier
  // orange-on-errors styling for callers that haven't opted in.
  trafficLight?: boolean;
}

// Bulk update uses #fccac7 / #fed3ca / #cdebda but red and amber are
// visually indistinguishable side-by-side. We diverge here: a clearly
// pink red and a yellow amber, so a glance at the preview tells you
// which rows are blocked vs only modified.
const TRAFFIC_LIGHT_COLORS = {
  red: "#fca5a5",
  amber: "#fde68a",
  green: "#cdebda",
};

function trafficLightStyle(
  row: UploadDefinition<any>,
): CSSProperties | undefined {
  // success===false rows are excluded from submit even when their errors[]
  // ends up empty (e.g. Yup catch with empty err.inner). Treat them as red
  // so the preview never lies about the submit outcome.
  if (row.success === false || (row.errors && row.errors.length > 0)) {
    return { backgroundColor: TRAFFIC_LIGHT_COLORS.red };
  }
  if (row.warnings && row.warnings.length > 0) {
    return { backgroundColor: TRAFFIC_LIGHT_COLORS.amber };
  }
  return { backgroundColor: TRAFFIC_LIGHT_COLORS.green };
}

const calculateStats = (
  definitions: UploadDefinition<any>[],
): { valid: number; errors: number } => {
  const errors = definitions.filter(
    (row) => row.errors && isArray(row.errors) && row.errors.length > 0,
  ).length;
  return {
    valid: definitions.length - errors,
    errors: errors,
  };
};

const errorRowIcon = (
  row: UploadDefinition<any>,
  rowIndex: number,
): React.ReactElement => {
  return <ErrorRowInfoTooltipIcon item={row} index={rowIndex} />;
};

const errorRow = (
  style: CSSProperties,
  onError = true,
): ConditionalStyles<UploadDefinition<any>> => {
  return {
    when: (row: any): boolean => {
      const errors = row.errors && isArray(row.errors) && row.errors.length > 0;
      if (onError) {
        return errors;
      } else {
        return !errors;
      }
    },
    style: style,
  };
};

const errorCell = (
  style: CSSProperties,
  key: string,
): ConditionalStyles<UploadDefinition<any>> => {
  return {
    when: (row: UploadDefinition<any>): boolean => {
      return row.errors
        ? row.errors?.filter((err) => {
            return err?.field === key;
          }).length > 0
        : false;
    },
    style: style,
  };
};

function UploadPreviewTable<T>(props: UploadPreviewTableProps<T>) {
  const {
    definitions,
    requiredHeaders,
    optionalHeaders = [],
    trafficLight = false,
  } = props;
  const columns: TableColumn<UploadDefinition<T>>[] = requiredHeaders
    .map((key: string): TableColumn<UploadDefinition<T>> => {
      return {
        name: key,
        selector: (row: UploadDefinition<T>): string =>
          isObject(row.original[key])
            ? JSON.stringify(row.original[key])
            : (row.original[key] as string),
        // In traffic-light mode the whole row is already painted red for
        // errors and the tooltip identifies the offending field, so the
        // per-cell orangeRed stripe just makes the row look two-tone.
        conditionalCellStyles: trafficLight
          ? []
          : [errorCell({ backgroundColor: "orangeRed" }, key)],
      };
    })
    .concat(
      definitions.length > 0
        ? Object.keys(definitions[0].original)
            .filter((key) => {
              return !requiredHeaders.includes(key);
            })
            .map((key) => {
              const ignored = !optionalHeaders.includes(key);
              return {
                name: (
                  <span>
                    {key} {ignored && <small>(ignored)</small>}
                  </span>
                ),
                selector: (row: any): string =>
                  isObject(row.original[key])
                    ? JSON.stringify(row.original[key])
                    : row.original[key],
                conditionalCellStyles: trafficLight
                  ? // Let the row tier (red/amber/green) bleed through optional
                    // columns. Only ignored columns get a muted grey override
                    // so they remain visually distinct from the columns that
                    // actually affect the upload.
                    ignored
                    ? [errorRow({ backgroundColor: "#e5e7eb" }, false)]
                    : []
                  : [
                      errorRow(
                        { backgroundColor: ignored ? "grey" : "white" },
                        false,
                      ),
                    ],
              };
            })
        : [],
    )
    .concat([
      {
        name: "Errors",
        cell: errorRowIcon,
      },
    ]);

  const conditionalRows: ConditionalStyles<UploadDefinition<T>>[] = trafficLight
    ? [{ when: () => true, style: trafficLightStyle }]
    : [errorRow({ backgroundColor: "orange" })];

  const stats = calculateStats(definitions);

  return (
    <>
      {stats.valid > 0 ? (
        <>
          <p>
            Your upload is ready. We will attempt to create {stats.valid}{" "}
            {props.uploadedEntityName}
            {stats.valid > 1 && "s"}.
          </p>
          {stats.errors !== 0 && (
            <p>
              <b>
                Warning: Your spreadsheet contains {stats.errors} row
                {stats.errors > 1 && "s"} with a detected error.
              </b>
            </p>
          )}
        </>
      ) : (
        <p>Your upload doesnt contain any valid rows. Please try again.</p>
      )}
      <Heading>Preview</Heading>
      <DataTable
        columns={columns}
        data={definitions}
        fixedHeader={false}
        pagination={false}
        responsive={true}
        dense={true}
        conditionalRowStyles={conditionalRows}
        customStyles={{ header: { style: { display: "none" } } }}
      />
    </>
  );
}

export default UploadPreviewTable;
